import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import './App.css';
import Layout from './Hoc/Layouts/Layout';
import Loader from './Components/UI/Loader/Loader';
import Jobs from './Containers/Jobs/Jobs';
import Landing from './Containers/Landing/Landing';
import NewDemand from './Containers/Demands/NewDemand';
import Profile from './Containers/Auth/Profile/Profile';
import Logout from './Containers/Auth/Logout/Logout';
import Login from './Containers/Auth/Login/Login';
import Signup from './Containers/Auth/SignUp/Signup';
import VerifyEmail from './Containers/VerifyEmail/VerifyEmail';
import RecoverPassword from './Containers/Auth/RecoverPassword/RecoverPassword';
import RegisterArtisan from './Containers/Auth/RegisterArtisan/RegisterArtisan';
import ProfileArtisan from './Containers/Auth/Profile/ProfileArtisan';
import NewJobs from './Containers/Jobs/NewJobs';
import Projects from './Containers/ClientProjects/Projects';
import Boutique from './Containers/Boutique/Boutique';
import CheckArtisanBoutique from './Containers/Boutique/CheckArtisanBoutique';
import ContractDetailsPDF from './Containers/ClientProjects/ContractPDF';
import DevisPDFComponent from './Containers/ClientProjects/DevisPDFComponent';
import CheckClientProfile from './Containers/ClientProjects/CheckClientProfile';
import PwaControls from './Components/PWA/PwaControls';
import LegalPage from './Containers/Legal/LegalPage';
import PublicDiscovery from './Containers/Discovery/PublicDiscovery';
import PageErrorBoundary from './Components/UI/ErrorBoundary/PageErrorBoundary';
import AuthActionHandler from './Containers/Auth/AuthActionHandler/AuthActionHandler';
import ArtisanAgenda from './Containers/Agenda/ArtisanAgenda';
import InvoicingCenter from './Containers/Invoicing/InvoicingCenter';
import AdminDataMigration from './Containers/Admin/AdminDataMigration';
import { useI18n } from './i18n/I18nProvider';

const LoadingScreen = () => {
  const { t } = useI18n();
  return (
  <div className="route-loader" role="status" aria-live="polite">
    <Loader label={t('app.loading')} />
  </div>
  );
};

const legalPaths = ['/terms', '/privacy', '/review-policy', '/fees', '/ranking'];

const PublicRoutes = () => (
  <Switch>
    <Route exact path="/auth/action" component={AuthActionHandler} />
    <Route exact path={["/", "/landing"]} component={Landing} />
    <Route exact path="/discover" component={PublicDiscovery} />
    <Route exact path={legalPaths} component={LegalPage} />
    <Route exact path="/login" component={Login} />
    <Route exact path="/signup" component={Signup} />
    <Route exact path="/registerartisan" component={RegisterArtisan} />
    <Route exact path="/recover" component={RecoverPassword} />
    <Redirect to="/" />
  </Switch>
);

const VerificationRoutes = () => (
  <Switch>
    <Route exact path="/auth/action" component={AuthActionHandler} />
    <Route exact path="/landing" component={Landing} />
    <Route exact path="/verify-email" component={VerifyEmail} />
    <Route exact path="/logout" component={Logout} />
    <Route exact path="/discover" component={PublicDiscovery} />
    <Route exact path={legalPaths} component={LegalPage} />
    <Redirect to="/verify-email" />
  </Switch>
);

const ClientRoutes = () => (
  <Switch>
    <Route exact path="/auth/action" component={AuthActionHandler} />
    <Route exact path="/landing" component={Landing} />
    <Route exact path="/profile" component={Profile} />
    <Route exact path="/projects" component={Projects} />
    <Route exact path="/artisanboutique" component={CheckArtisanBoutique} />
    <Route exact path="/clientprofile" component={CheckClientProfile} />
    <Route exact path="/contract" component={ContractDetailsPDF} />
    <Route exact path="/devis" component={DevisPDFComponent} />
    <Route exact path={["/demands", "/newDemand"]} component={NewDemand} />
    <Route exact path="/logout" component={Logout} />
    <Route exact path="/discover" component={PublicDiscovery} />
    <Route exact path={legalPaths} component={LegalPage} />
    <Redirect exact from="/" to="/demands" />
    <Redirect to="/demands" />
  </Switch>
);

const ArtisanRoutes = ({ isAdmin, onboardingComplete }) => (
  <Switch>
    <Route exact path="/auth/action" component={AuthActionHandler} />
    <Route exact path="/landing" component={Landing} />
    <Route exact path="/profileartisan" component={ProfileArtisan} />
    {!onboardingComplete && <Route exact path="/logout" component={Logout} />}
    {!onboardingComplete && <Route exact path={legalPaths} component={LegalPage} />}
    {!onboardingComplete && <Redirect to="/profileartisan" />}
    <Route exact path="/jobs" component={Jobs} />
    <Route exact path="/planning" component={ArtisanAgenda} />
    <Route exact path="/invoicing" component={InvoicingCenter} />
    {isAdmin && <Route exact path="/admin/data-migration" component={AdminDataMigration} />}
    <Route exact path="/newjobs" component={NewJobs} />
    <Route exact path="/boutique" component={Boutique} />
    <Route exact path="/artisanboutique" component={CheckArtisanBoutique} />
    <Route exact path="/clientprofile" component={CheckClientProfile} />
    <Route exact path="/contract" component={ContractDetailsPDF} />
    <Route exact path="/devis" component={DevisPDFComponent} />
    <Route exact path="/logout" component={Logout} />
    <Route exact path="/discover" component={PublicDiscovery} />
    <Route exact path={legalPaths} component={LegalPage} />
    <Redirect exact from="/" to="/newjobs" />
    <Redirect to="/newjobs" />
  </Switch>
);

function App({ artisanOnboardingComplete, authLoaded, emailVerified, isAdmin, loggedIn, profileLoaded, userType }) {
  if (!authLoaded || (loggedIn && !profileLoaded)) {
    return <LoadingScreen />;
  }

  let routes = <PublicRoutes />;

  if (loggedIn && !emailVerified) {
    routes = <VerificationRoutes />;
  } else if (loggedIn && emailVerified && userType === 'Client') {
    routes = <ClientRoutes />;
  } else if (loggedIn && emailVerified && userType === 'Artisan') {
    routes = <ArtisanRoutes isAdmin={isAdmin} onboardingComplete={artisanOnboardingComplete} />;
  }

  return (
    <Layout>
      <ToastContainer
        position="top-right"
        autoClose={4500}
        closeOnClick
        pauseOnFocusLoss
        pauseOnHover
        theme="light"
      />
      <PageErrorBoundary>{routes}</PageErrorBoundary>
      <PwaControls />
    </Layout>
  );
}

const mapStateToProps = ({ firebase }) => ({
  authLoaded: Boolean(firebase.auth && firebase.auth.isLoaded),
  profileLoaded: Boolean(firebase.profile && firebase.profile.isLoaded),
  userType: firebase.profile && firebase.profile.user_type,
  loggedIn: Boolean(firebase.auth && firebase.auth.uid),
  emailVerified: Boolean(firebase.auth && firebase.auth.emailVerified),
  isAdmin: Boolean(firebase.profile && firebase.profile.is_admin === true),
  artisanOnboardingComplete: Boolean(
    firebase.profile
      && firebase.profile.speciality
      && firebase.profile.artisan_region
  ),
});

export default connect(mapStateToProps)(App);
